import React, { useEffect, useMemo, useState } from 'react';
import { reportsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Loader2, Trophy, Users, MapPin, Percent, Euro,
    Download, Printer, Target, CalendarDays, CalendarRange
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, LabelList,
} from 'recharts';
import { toast } from 'sonner';
import { exportToCSV } from '../lib/exportCsv';
import { apiErrorMessage } from '../lib/apiError';
import { ModelsReport } from '../components/reports/ModelsReport';

const fmtEuro = (n) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;
const fmtK = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v || 0}`);

const KpiCard = ({ icon: Icon, label, value, hint, accent = 'default', testid }) => {
    const accentClasses = {
        default: 'text-foreground',
        success: 'text-emerald-600',
        danger: 'text-red-600',
        primary: 'text-primary',
    };
    return (
        <Card data-testid={testid}>
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
                    <Icon size={18} className="text-muted-foreground" />
                </div>
                <div className={`text-2xl font-heading font-bold ${accentClasses[accent]}`}>{value}</div>
                {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
            </CardContent>
        </Card>
    );
};

const ReportsPage = () => {
    const { isAdmin, canViewTeam } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [users, setUsers] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [userFilter, setUserFilter] = useState('all');

    const load = async (params) => {
        setLoading(true);
        try {
            const effective = {
                dateFrom: params?.dateFrom ?? dateFrom,
                dateTo: params?.dateTo ?? dateTo,
                userId: (params?.userFilter ?? userFilter) !== 'all' ? (params?.userFilter ?? userFilter) : undefined,
            };
            const queryParams = {
                dateFrom: effective.dateFrom ? new Date(effective.dateFrom).toISOString() : undefined,
                dateTo: effective.dateTo ? new Date(`${effective.dateTo}T23:59:59`).toISOString() : undefined,
                userId: effective.userId,
            };
            const { data } = await reportsAPI.get(queryParams);
            setData(data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Errore nel caricamento report'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadInitial = async () => {
            if (isAdmin()) {
                try {
                    const res = await usersAPI.getAll();
                    setUsers(res.data);
                } catch (e) {
                    console.warn('users load failed', e);
                }
            } else if (canViewTeam()) {
                try {
                    const res = await usersAPI.getSellers();
                    setUsers(res.data);
                } catch (e) {
                    console.warn('sellers load failed', e);
                }
            }
            await load();
        };
        loadInitial();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => load();
    const resetFilters = () => {
        setDateFrom('');
        setDateTo('');
        setUserFilter('all');
        load({ dateFrom: '', dateTo: '', userFilter: 'all' });
    };

    const summary = data?.summary;
    const byUser = data?.by_user || [];
    const byCountry = data?.by_country || [];
    const salesMonthly = data?.sales_monthly || [];
    const salesWeekly = data?.sales_weekly || [];

    const byUserChartData = useMemo(() =>
        byUser.map(u => ({ name: u.name.split(' ')[0], value: u.won_value })),
        [byUser]
    );

    const handlePrint = () => window.print();

    const handleExportSummary = () => {
        if (!summary) return;
        const rows = [{
            'Clienti totali': summary.total_clients,
            'Clienti attivi': summary.active_clients,
            'Clienti persi': summary.lost_clients,
            'Offerte confermate': summary.won_opportunities,
            'Offerte perse': summary.lost_opportunities,
            'Valore confermato (EUR)': summary.won_value,
            'Valore perso (EUR)': summary.lost_value,
            'Tasso di vittoria (%)': summary.win_rate.toFixed(2),
            'Ticket medio (EUR)': summary.avg_deal_size,
        }];
        exportToCSV('report_sintesi', rows);
    };

    const handleExportMonthly = () => {
        const rows = salesMonthly.map(m => ({ Mese: m.label, 'Offerte confermate': m.count, 'Valore (EUR)': m.value }));
        exportToCSV('vendite_mensili', rows);
    };

    const handleExportAnnual = async () => {
        try {
            const year = new Date().getFullYear();
            const res = await reportsAPI.downloadAnnualExcel(year);
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Report_Annuale_${year}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Errore nel download del report annuale');
        }
    };

    const handleExportByUser = () => {
        const rows = byUser.map(u => ({
            Commerciale: u.name,
            Clienti: u.clients,
            Confermate: u.won_count,
            Perse: u.lost_count,
            'Valore confermato (EUR)': u.won_value,
            'Win Rate (%)': u.win_rate.toFixed(2),
        }));
        exportToCSV('report_per_commerciale', rows);
    };

    const handleExportByCountry = () => {
        const rows = byCountry.map(c => ({
            Paese: c.country,
            Clienti: c.clients,
            'Valore confermato (EUR)': c.won_value,
        }));
        exportToCSV('report_per_paese', rows);
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 print:space-y-4" data-testid="reports-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">Report Online</h1>
                    <p className="text-muted-foreground">Analisi vendite e offerte confermate</p>
                </div>
                <div className="flex flex-wrap gap-2 print:hidden">
                    <Button variant="outline" onClick={handlePrint} data-testid="report-print-btn">
                        <Printer size={16} className="mr-2" /> Stampa / PDF
                    </Button>
                    <Button variant="outline" onClick={handleExportSummary} data-testid="report-export-summary">
                        <Download size={16} className="mr-2" /> Esporta Sintesi
                    </Button>
                    <Button variant="outline" onClick={handleExportAnnual} data-testid="report-export-annual">
                        <Download size={16} className="mr-2" /> Esporta Anno (Excel)
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="print:hidden">
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Da</Label>
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                data-testid="report-date-from"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">A</Label>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                data-testid="report-date-to"
                            />
                        </div>
                        {canViewTeam() && (
                            <div>
                                <Label className="text-xs uppercase tracking-wider mb-2 block">Commerciale</Label>
                                <Select value={userFilter} onValueChange={setUserFilter}>
                                    <SelectTrigger data-testid="report-user-filter">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tutti</SelectItem>
                                        {users.filter(u => u.role !== 'addetto_consegna').map(u => (
                                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Button className="btn-primary flex-1" onClick={applyFilters} data-testid="report-apply">
                                APPLICA
                            </Button>
                            <Button variant="outline" onClick={resetFilters} data-testid="report-reset">
                                Reset
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* KPI Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={Users} label="Clienti totali" value={summary.total_clients} hint={`${summary.active_clients} attivi · ${summary.lost_clients} persi`} testid="kpi-clients" />
                <KpiCard icon={Trophy} label="Offerte confermate" value={summary.won_opportunities} hint={fmtEuro(summary.won_value)} accent="success" testid="kpi-won" />
                <KpiCard icon={Euro} label="Valore confermato" value={fmtEuro(summary.won_value)} hint="Totale venduto nel periodo" accent="primary" testid="kpi-value" />
                <KpiCard icon={CalendarRange} label="Ticket medio" value={fmtEuro(summary.avg_deal_size)} hint="Per offerta confermata" testid="kpi-avg" />
            </div>

            {/* Vendite mensili + settimanali (importi) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                            <CalendarRange size={16} /> Vendite Mensili (importo)
                        </CardTitle>
                        <Button variant="outline" size="sm" onClick={handleExportMonthly} className="print:hidden" data-testid="report-export-monthly">
                            <Download size={14} className="mr-2" /> CSV
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {salesMonthly.length === 0 ? (
                            <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">
                                Nessuna vendita nel periodo selezionato
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={salesMonthly} margin={{ top: 16, right: 10, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="label" fontSize={12} />
                                    <YAxis tickFormatter={fmtK} fontSize={12} />
                                    <Tooltip formatter={(v) => fmtEuro(v)} />
                                    <Bar dataKey="value" name="Venduto" fill="#D32F2F" radius={[4, 4, 0, 0]}>
                                        <LabelList dataKey="value" position="top" formatter={(v) => (v ? fmtK(v) : '')} fontSize={11} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                            <CalendarDays size={16} /> Vendite Settimanali — ultime 8 (importo)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {salesWeekly.length === 0 ? (
                            <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">
                                Nessun dato
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={salesWeekly} margin={{ top: 16, right: 10, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="label" fontSize={12} />
                                    <YAxis tickFormatter={fmtK} fontSize={12} />
                                    <Tooltip formatter={(v) => fmtEuro(v)} />
                                    <Bar dataKey="value" name="Venduto" fill="#1976D2" radius={[4, 4, 0, 0]}>
                                        <LabelList dataKey="value" position="top" formatter={(v) => (v ? fmtK(v) : '')} fontSize={11} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* By User */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                        <Trophy size={16} /> Performance Commerciali
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={handleExportByUser} className="print:hidden" data-testid="report-export-by-user">
                        <Download size={14} className="mr-2" /> CSV
                    </Button>
                </CardHeader>
                <CardContent>
                    {byUser.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nessun dato</p>
                    ) : (
                        <>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={byUserChartData} margin={{ top: 16, right: 10, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" fontSize={12} />
                                    <YAxis tickFormatter={fmtK} fontSize={12} />
                                    <Tooltip formatter={(v) => fmtEuro(v)} />
                                    <Bar dataKey="value" name="Confermato" fill="#10B981" radius={[4, 4, 0, 0]}>
                                        <LabelList dataKey="value" position="top" formatter={(v) => (v ? fmtK(v) : '')} fontSize={11} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="mt-4 overflow-x-auto">
                                <table className="w-full text-sm data-table">
                                    <thead>
                                        <tr className="border-b bg-muted/30">
                                            <th className="text-left py-3 px-3">Commerciale</th>
                                            <th className="text-right py-3 px-3">Clienti</th>
                                            <th className="text-right py-3 px-3">Confermate</th>
                                            <th className="text-right py-3 px-3">Perse</th>
                                            <th className="text-right py-3 px-3">Valore confermato</th>
                                            <th className="text-right py-3 px-3">Win Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byUser.map((u) => (
                                            <tr key={u.user_id} className="border-b hover:bg-muted/20">
                                                <td className="py-2 px-3 font-medium">{u.name}</td>
                                                <td className="py-2 px-3 text-right">{u.clients}</td>
                                                <td className="py-2 px-3 text-right text-emerald-600 font-medium">{u.won_count}</td>
                                                <td className="py-2 px-3 text-right text-red-600">{u.lost_count}</td>
                                                <td className="py-2 px-3 text-right font-medium">{fmtEuro(u.won_value)}</td>
                                                <td className="py-2 px-3 text-right">
                                                    <Badge variant="outline">{fmtPct(u.win_rate)}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* By Country */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                        <MapPin size={16} /> Analisi Geografica
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={handleExportByCountry} className="print:hidden" data-testid="report-export-by-country">
                        <Download size={14} className="mr-2" /> CSV
                    </Button>
                </CardHeader>
                <CardContent>
                    {byCountry.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nessun dato</p>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            <div className="lg:col-span-2">
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie
                                            data={byCountry.slice(0, 8)}
                                            dataKey="clients"
                                            nameKey="country"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={90}
                                            label={(e) => e.country}
                                        >
                                            {byCountry.slice(0, 8).map((_, i) => (
                                                <Cell key={i} fill={[
                                                    '#D32F2F', '#1976D2', '#388E3C', '#F57C00',
                                                    '#7B1FA2', '#0097A7', '#C2185B', '#5D4037'
                                                ][i % 8]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="lg:col-span-3 overflow-x-auto">
                                <table className="w-full text-sm data-table">
                                    <thead>
                                        <tr className="border-b bg-muted/30">
                                            <th className="text-left py-3 px-3">Paese</th>
                                            <th className="text-right py-3 px-3">Clienti</th>
                                            <th className="text-right py-3 px-3">Valore confermato</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byCountry.map((c) => (
                                            <tr key={c.country} className="border-b hover:bg-muted/20">
                                                <td className="py-2 px-3 font-medium">{c.country}</td>
                                                <td className="py-2 px-3 text-right">{c.clients}</td>
                                                <td className="py-2 px-3 text-right font-medium text-emerald-600">{fmtEuro(c.won_value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Modelli venduti */}
            <ModelsReport />
        </div>
    );
};

export default ReportsPage;
